/**
 * Local Models Page
 *
 * Every model Free mode can download: its size, where it runs,
 * where it comes from and the license its weights carry (kizuna-ai-lab/sokuji#584).
 * The rows come from lib/local-models.ts, exported from the sokuji repo.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useI18n } from '@/lib/i18n';
import { useDocumentMeta } from '@/lib/document-meta';
import {
  BUNDLED_COMPONENTS,
  CATALOG_VERSION,
  LOCAL_MODELS,
  MODEL_TYPES,
  TIERS,
  formatBytes,
  languageLabel,
  type LicenseTier,
  type LocalModel,
} from '@/lib/local-models';
import './docs.scss';

function OutLink({ href, children }: { href?: string; children: ReactNode }) {
  if (!href) return <>{children}</>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function matches(model: LocalModel, query: string): boolean {
  return [model.name, model.id, model.upstream, model.license, model.sourceLang, model.targetLang, ...model.languages]
    .some((field) => field?.toLowerCase().includes(query));
}

export function LocalModels() {
  const { t } = useI18n();
  useDocumentMeta(t('localModels.title'), t('localModels.intro'));

  const [tiers, setTiers] = useState<ReadonlySet<LicenseTier>>(() => new Set(TIERS));
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LOCAL_MODELS.filter((m) => tiers.has(m.tier) && (!q || matches(m, q)));
  }, [tiers, query]);

  const toggleTier = (tier: LicenseTier) => {
    const next = new Set(tiers);
    if (next.has(tier)) next.delete(tier);
    else next.add(tier);
    // Switching the last one off would hide everything; show all again instead.
    setTiers(next.size ? next : new Set(TIERS));
  };

  const tierChip = (tier: LicenseTier) => (
    <span className={`local-models__tier local-models__tier--${tier}`}>{t(`localModels.tier.${tier}.label`)}</span>
  );

  return (
    <div className="docs-content local-models">
      <h1>{t('localModels.title')}</h1>
      <p className="local-models__intro">{t('localModels.intro')}</p>

      <div className="local-models__info">
        <section>
          <h2>{t('localModels.runtimeTitle')}</h2>
          <p>{t('localModels.runtimeBody')}</p>
          <p>{t('localModels.variantsNote')}</p>
        </section>
        <section>
          <h2>{t('localModels.licenseTitle')}</h2>
          <p>{t('localModels.licenseBody')}</p>
          <dl className="local-models__legend">
            {TIERS.map((tier) => (
              <div key={tier}>
                <dt>{tierChip(tier)}</dt>
                <dd>{t(`localModels.tier.${tier}.desc`)}</dd>
              </div>
            ))}
          </dl>
          <p className="local-models__fineprint">{t('localModels.flagNcBase')}</p>
        </section>
      </div>

      <div className="local-models__filters">
        <div className="local-models__tier-toggles" role="group" aria-label={t('localModels.filterLabel')}>
          {TIERS.map((tier) => (
            <button
              key={tier}
              type="button"
              aria-pressed={tiers.has(tier)}
              className={`local-models__tier local-models__tier--${tier}`}
              onClick={() => toggleTier(tier)}
            >
              {t(`localModels.tier.${tier}.label`)}
            </button>
          ))}
        </div>
        <input
          id="local-models-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('localModels.search')}
          aria-label={t('localModels.search')}
        />
        <span className="local-models__count" aria-live="polite">
          {t('localModels.showing').replace('{count}', String(shown.length))}
        </span>
      </div>

      {MODEL_TYPES.map((type) => {
        const rows = shown.filter((m) => m.type === type);
        if (rows.length === 0) return null;
        return (
          <section key={type} id={type} className="local-models__section">
            <h2>
              {t(`localModels.type.${type}`)} <span className="local-models__section-count">{rows.length}</span>
            </h2>
            <div className="local-models__table-wrap">
              <table className="local-models__table">
                <thead>
                  <tr>
                    <th scope="col">{t('localModels.col.model')}</th>
                    <th scope="col">{t('localModels.col.languages')}</th>
                    <th scope="col">{t('localModels.col.runtime')}</th>
                    <th scope="col">{t('localModels.col.size')}</th>
                    <th scope="col">{t('localModels.col.source')}</th>
                    <th scope="col">{t('localModels.col.license')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <Fragment key={m.id}>
                      <tr className={m.note || m.flags ? 'local-models__row--noted' : undefined}>
                        <th scope="row">{m.name}</th>
                        <td data-label={t('localModels.col.languages')}>{languageLabel(m) ?? t('localModels.multilingual')}</td>
                        <td data-label={t('localModels.col.runtime')}>{t(`localModels.runtime.${m.runtime}`)}</td>
                        <td className="local-models__size" data-label={t('localModels.col.size')}>
                          {m.variants.length === 0
                            ? '—'
                            : m.variants.map((v) => (
                                <div key={v.dtype}>
                                  {m.variants.length > 1 && <span className="local-models__dtype">{v.dtype}</span>}
                                  {formatBytes(v.bytes)}
                                </div>
                              ))}
                        </td>
                        <td className="local-models__source" data-label={t('localModels.col.source')}>
                          <OutLink href={m.upstreamUrl}>{m.upstream}</OutLink>
                          {m.revision && <span className="local-models__revision">{m.revision}</span>}
                          {m.via && (
                            <div className="local-models__via">
                              {t('localModels.package')}: <OutLink href={m.viaUrl}>{m.via}</OutLink>
                            </div>
                          )}
                        </td>
                        <td className="local-models__license" data-label={t('localModels.col.license')}>
                          <OutLink href={m.licenseUrl}>{m.license}</OutLink>
                          <div>{tierChip(m.tier)}</div>
                        </td>
                      </tr>
                      {/* The note spans the row so it is never the column scrolled out of view. */}
                      {(m.note || m.flags) && (
                        <tr className="local-models__note-row">
                          <td colSpan={6}>
                            <span className="local-models__note-label">{t('localModels.col.notes')}:</span> {m.note}
                            {m.flags?.map((flag) => (
                              <span key={flag} className="local-models__flag">{t(`localModels.flag.${flag}`)}</span>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {shown.length === 0 && <p className="local-models__empty">{t('localModels.noMatch')}</p>}

      <section className="local-models__bundled">
        <p>{t('localModels.bundled')}</p>
        <ul>
          {BUNDLED_COMPONENTS.map((c) => (
            <li key={c.id}>
              <OutLink href={c.upstreamUrl}>{c.upstream}</OutLink> · <OutLink href={c.licenseUrl}>{c.license}</OutLink>
            </li>
          ))}
        </ul>
      </section>

      <p className="local-models__fineprint">{t('localModels.generatedFrom').replace('{version}', CATALOG_VERSION)}</p>
    </div>
  );
}
